import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";
import { money, thisMonth } from "../../lib/format";

const TAGS = ["Groceries", "Household Supplies", "Kitchen", "Cleaning", "Furniture", "Appliances", "Repairs", "Other"];

export default function HouseholdManager({ open, onOpenChange, month, currency, onChanged, readOnly }) {
  const [items, setItems] = useState([]);
  const [item, setItem] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Groceries");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(`${month}-01`);

  const load = useCallback(async () => {
    const { data } = await api.get(`/household?month=${month}`);
    setItems(data);
  }, [month]);

  useEffect(() => { if (open) { load(); setDate(month === thisMonth() ? new Date().toISOString().slice(0, 10) : `${month}-01`); } }, [open, load, month]);

  const add = async () => {
    if (readOnly) return toast.error("View-only account — editing is disabled.");
    if (!item || !amount) return toast.error("Enter item and amount");
    await api.post("/household", { item, amount: Number(amount), category, note, date, month });
    setItem(""); setAmount(""); setNote("");
    toast.success("Household expense logged");
    load(); onChanged();
  };

  const del = async (id) => { if (readOnly) return toast.error("View-only account — editing is disabled."); await api.delete(`/household/${id}`); load(); onChanged(); };

  const total = items.reduce((s, i) => s + Number(i.amount), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display">Household Wallet</DialogTitle>
          <DialogDescription>Record groceries and anything for the house. Totals feed into your spending cap and My Wallet.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-end bg-slate-50 p-3 rounded-xl">
          <Input data-testid="household-item-input" placeholder="Item e.g. Weekly groceries" value={item} onChange={(e) => setItem(e.target.value)} disabled={readOnly} />
          <Input data-testid="household-amount-input" type="number" placeholder="Amount" className="font-mono-nums" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={readOnly} />
          <Select value={category} onValueChange={setCategory} disabled={readOnly}>
            <SelectTrigger data-testid="household-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="household-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={readOnly} />
          <Input data-testid="household-note-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} disabled={readOnly} />
          <Button data-testid="household-add-button" onClick={add} disabled={readOnly} className="text-white" style={{ background: "#65A30D" }}><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-slate-500">{items.length} item(s)</span>
          <span className="font-mono-nums font-semibold" style={{ color: "#4D7C0F" }}>Total: {money(total, currency)}</span>
        </div>

        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} data-testid={`household-row-${i.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 text-sm truncate">{i.item}</p>
                <p className="text-xs text-slate-400">{i.category} · {i.date}{i.note ? ` · ${i.note}` : ""}</p>
              </div>
              <span className="font-mono-nums font-semibold text-slate-800 text-sm">{money(i.amount, currency)}</span>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(i.id)} data-testid={`household-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-sm text-slate-400 py-6">No household expenses logged this month.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
